import type { AuthUser } from '@/types/auth';
import type {
  ChangeEventSummary,
  ChangeEventDetail,
  ReviewSessionSummary,
  DepartmentStatus,
  AttestationRecord,
  PublishQueueEntry,
  PaginatedResponse,
  DiffReviewPackage,
  ConfirmationPayload,
  ReviewerRecord,
  RegisterReviewerInput,
  ConnectorConfig,
  ConnectorConfigInput,
  TenantRecord,
  ArchiveQueryResponse,
  WorkflowProject,
  WorkflowProjectSummary,
  WorkflowDeptProgress,
  WorkflowTask,
  WorkflowChecklistItem,
  WorkflowNotification,
  WorkflowEscalation,
  WorkflowEscalationRule,
  WorkflowDashboard,
  WorkflowAuditEntry,
  NotificationPreferences,
  TenantConnector,
  ConnectorFileEntry,
  PolicyReviewSession,
  PolicySectionReview,
} from '@/types/api';

export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;

  constructor(status: number, body: unknown) {
    super(`API error ${status}`);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

async function apiFetch<T>(
  path: string,
  options?: RequestInit,
): Promise<T> {
  const res = await fetch(`/api${path}`, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers,
    },
  });

  if (!res.ok) {
    let body: unknown;
    try {
      body = await res.json();
    } catch {
      body = await res.text();
    }
    throw new ApiError(res.status, body);
  }

  if (res.status === 204) {
    return undefined as T;
  }

  return res.json() as Promise<T>;
}

interface ListFilters {
  readonly [key: string]: string | number | boolean | undefined;
}

function buildQuery(filters?: ListFilters): string {
  if (!filters) return '';
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value !== undefined) {
      params.set(key, String(value));
    }
  }
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

export const api = {
  auth: {
    login: async (email: string, password: string): Promise<AuthUser> => {
      const result = await apiFetch<{ token: string; user: AuthUser }>(
        '/auth/login',
        {
          method: 'POST',
          body: JSON.stringify({ email, password }),
        },
      );
      return result.user;
    },

    logout: (): Promise<void> =>
      apiFetch<void>('/auth/logout', { method: 'POST' }),

    me: (): Promise<AuthUser> =>
      apiFetch<AuthUser>('/auth/me'),

    signup: async (input: { orgName: string; orgSize: string; adminEmail: string; adminName: string; password: string; planType: string }): Promise<{ token: string; user: AuthUser }> =>
      apiFetch<{ token: string; user: AuthUser }>('/auth/signup', { method: 'POST', body: JSON.stringify(input) }),

    invite: (input: { email: string; role: string }): Promise<{ success: boolean }> =>
      apiFetch<{ success: boolean }>('/auth/invite', { method: 'POST', body: JSON.stringify(input) }),

    validateInvite: (token: string): Promise<Record<string, unknown>> =>
      apiFetch<Record<string, unknown>>(`/auth/invite/${token}`),

    acceptInvite: (token: string, input: { name: string; password: string }): Promise<{ token: string; user: AuthUser }> =>
      apiFetch<{ token: string; user: AuthUser }>(`/auth/invite/${token}/accept`, { method: 'POST', body: JSON.stringify(input) }),
  },

  changeEvents: {
    list: (filters?: ListFilters): Promise<PaginatedResponse<ChangeEventSummary>> =>
      apiFetch<PaginatedResponse<ChangeEventSummary>>(`/change-events${buildQuery(filters)}`),

    get: (id: string): Promise<ChangeEventDetail> =>
      apiFetch<ChangeEventDetail>(`/change-events/${id}`),
  },

  reviewSessions: {
    list: (filters?: ListFilters): Promise<PaginatedResponse<ReviewSessionSummary>> =>
      apiFetch<PaginatedResponse<ReviewSessionSummary>>(`/review/sessions${buildQuery(filters)}`),

    get: (id: string): Promise<DiffReviewPackage> =>
      apiFetch<DiffReviewPackage>(`/review/sessions/${id}`),

    approve: (id: string, notes?: string): Promise<void> =>
      apiFetch<void>(`/review/sessions/${id}/approve`, {
        method: 'POST',
        body: JSON.stringify({ notes }),
      }),

    requestRevision: (id: string, notes: string): Promise<void> =>
      apiFetch<void>(`/review/sessions/${id}/revise`, {
        method: 'POST',
        body: JSON.stringify({ notes }),
      }),

    escalate: (id: string, notes: string): Promise<void> =>
      apiFetch<void>(`/review/sessions/${id}/escalate`, {
        method: 'POST',
        body: JSON.stringify({ notes }),
      }),
  },

  publishQueue: {
    list: (filters?: ListFilters): Promise<PaginatedResponse<PublishQueueEntry>> =>
      apiFetch<PaginatedResponse<PublishQueueEntry>>(`/publish/queue${buildQuery(filters)}`),

    buildConfirmation: (entryIds: readonly string[]): Promise<ConfirmationPayload> =>
      apiFetch<ConfirmationPayload>('/publish/confirm', {
        method: 'POST',
        body: JSON.stringify({ entryIds }),
      }),

    confirm: (
      confirmationId: string,
      entryIds: readonly string[],
    ): Promise<void> =>
      apiFetch<void>('/publish/execute', {
        method: 'POST',
        body: JSON.stringify({ confirmationId, entryIds }),
      }),
  },

  attestations: {
    list: (filters?: ListFilters): Promise<PaginatedResponse<AttestationRecord>> =>
      apiFetch<PaginatedResponse<AttestationRecord>>(`/attestations${buildQuery(filters)}`),

    acknowledge: (id: string): Promise<void> =>
      apiFetch<void>(`/attestations/${id}/acknowledge`, {
        method: 'POST',
      }),
  },

  departments: {
    list: (): Promise<DepartmentStatus[]> =>
      apiFetch<DepartmentStatus[]>('/departments'),

    getStatus: (department: string): Promise<DepartmentStatus> =>
      apiFetch<DepartmentStatus>(`/departments/${department}/status`),

    updateMode: (
      department: string,
      mode: string,
    ): Promise<{ department: string; mode: string; updatedAt: string }> =>
      apiFetch<{ department: string; mode: string; updatedAt: string }>(
        `/departments/${department}/mode`,
        { method: 'PATCH', body: JSON.stringify({ mode }) },
      ),
  },

  archive: {
    query: (queryText: string): Promise<ArchiveQueryResponse> =>
      apiFetch<ArchiveQueryResponse>('/archive/query', {
        method: 'POST',
        body: JSON.stringify({ query: queryText }),
      }),

    getVersionHistory: (documentId: string): Promise<{ versions: unknown[] }> =>
      apiFetch<{ versions: unknown[] }>(`/archive/documents/${documentId}/versions`),
  },

  ai: {
    generateChecklist: (taskId: string) =>
      apiFetch<{ items: WorkflowChecklistItem[]; count: number }>('/ai/generate-checklist', {
        method: 'POST',
        body: JSON.stringify({ taskId }),
      }),

    summarizeProject: (projectId: string) =>
      apiFetch<{ summary: string }>('/ai/summarize-project', {
        method: 'POST',
        body: JSON.stringify({ projectId }),
      }),

    generateProject: (input: Record<string, unknown>) =>
      apiFetch<{ project: WorkflowProject; tasks: WorkflowTask[]; summary: string; taskCount: number }>(
        '/ai/generate-project',
        { method: 'POST', body: JSON.stringify(input) },
      ),

    explainRegulation: (input: {
      documentType: string;
      sourceName: string;
      frameworksAffected: string[];
      urgencyScore: number;
      effectiveDate: string | null;
      affectedDepartments: string[];
      affectedCardCount: number;
    }): Promise<{ bullets: string[] }> =>
      apiFetch<{ bullets: string[] }>('/ai/explain-regulation', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
  },

  projects: {
    list: (filters?: ListFilters) =>
      apiFetch<{ projects: WorkflowProject[]; total: number }>(`/projects${buildQuery(filters)}`),

    get: (id: string) =>
      apiFetch<WorkflowProject>(`/projects/${id}`),

    create: (input: Record<string, unknown>) =>
      apiFetch<WorkflowProject>('/projects', { method: 'POST', body: JSON.stringify(input) }),

    update: (id: string, input: Record<string, unknown>) =>
      apiFetch<WorkflowProject>(`/projects/${id}`, { method: 'PATCH', body: JSON.stringify(input) }),

    cancel: (id: string) =>
      apiFetch<{ success: boolean }>(`/projects/${id}/cancel`, { method: 'POST' }),

    summary: () =>
      apiFetch<WorkflowProjectSummary>('/projects/summary'),

    departmentProgress: () =>
      apiFetch<{ departments: WorkflowDeptProgress[] }>('/projects/department-progress'),
  },

  tasks: {
    list: (filters?: ListFilters) =>
      apiFetch<{ tasks: WorkflowTask[]; total: number }>(`/tasks${buildQuery(filters)}`),

    get: (id: string) =>
      apiFetch<WorkflowTask>(`/tasks/${id}`),

    create: (input: Record<string, unknown>) =>
      apiFetch<WorkflowTask>('/tasks', { method: 'POST', body: JSON.stringify(input) }),

    update: (id: string, input: Record<string, unknown>) =>
      apiFetch<WorkflowTask>(`/tasks/${id}`, { method: 'PATCH', body: JSON.stringify(input) }),

    assign: (id: string, userId: string) =>
      apiFetch<WorkflowTask>(`/tasks/${id}/assign`, { method: 'POST', body: JSON.stringify({ userId }) }),

    autoAssign: (id: string) =>
      apiFetch<WorkflowTask>(`/tasks/${id}/auto-assign`, { method: 'POST' }),

    checklist: (taskId: string) =>
      apiFetch<{ items: WorkflowChecklistItem[] }>(`/tasks/${taskId}/checklist`),

    addChecklistItem: (taskId: string, input: Record<string, unknown>) =>
      apiFetch<WorkflowChecklistItem>(`/tasks/${taskId}/checklist`, { method: 'POST', body: JSON.stringify(input) }),

    updateChecklistItem: (taskId: string, itemId: string, input: Record<string, unknown>) =>
      apiFetch<WorkflowChecklistItem>(`/tasks/${taskId}/checklist/${itemId}`, { method: 'PATCH', body: JSON.stringify(input) }),

    attest: (taskId: string, statement?: string) =>
      apiFetch<unknown>(`/tasks/${taskId}/attest`, { method: 'POST', body: JSON.stringify({ statement }) }),

    getAttestation: (taskId: string) =>
      apiFetch<unknown>(`/tasks/${taskId}/attestation`),
  },

  notifications: {
    list: (filters?: ListFilters) =>
      apiFetch<{ notifications: WorkflowNotification[]; total: number }>(`/notifications${buildQuery(filters)}`),

    unreadCount: () =>
      apiFetch<{ count: number }>('/notifications/unread-count'),

    markRead: (id: string) =>
      apiFetch<{ success: boolean }>(`/notifications/${id}/read`, { method: 'POST' }),

    markAllRead: () =>
      apiFetch<{ success: boolean }>('/notifications/read-all', { method: 'POST' }),

    preferences: () =>
      apiFetch<{ preferences: NotificationPreferences }>('/notifications/preferences'),

    updatePreferences: (updates: Record<string, unknown>) =>
      apiFetch<{ preferences: NotificationPreferences }>('/notifications/preferences', {
        method: 'PATCH',
        body: JSON.stringify(updates),
      }),
  },

  escalations: {
    list: () =>
      apiFetch<{ escalations: WorkflowEscalation[]; total: number }>('/escalations'),

    mine: () =>
      apiFetch<{ escalations: WorkflowEscalation[] }>('/escalations/mine'),

    create: (input: Record<string, unknown>) =>
      apiFetch<WorkflowEscalation>('/escalations', { method: 'POST', body: JSON.stringify(input) }),

    acknowledge: (id: string) =>
      apiFetch<{ success: boolean }>(`/escalations/${id}/acknowledge`, { method: 'POST' }),

    resolve: (id: string) =>
      apiFetch<{ success: boolean }>(`/escalations/${id}/resolve`, { method: 'POST' }),

    rules: {
      list: () =>
        apiFetch<{ rules: WorkflowEscalationRule[] }>('/escalations/rules'),

      create: (input: Record<string, unknown>) =>
        apiFetch<WorkflowEscalationRule>('/escalations/rules', { method: 'POST', body: JSON.stringify(input) }),

      update: (ruleId: string, input: Record<string, unknown>) =>
        apiFetch<WorkflowEscalationRule>(`/escalations/rules/${ruleId}`, { method: 'PATCH', body: JSON.stringify(input) }),

      delete: (ruleId: string) =>
        apiFetch<{ success: boolean }>(`/escalations/rules/${ruleId}`, { method: 'DELETE' }),
    },
  },

  dashboard: {
    overview: () =>
      apiFetch<WorkflowDashboard>('/dashboard'),

    auditLog: (filters?: ListFilters) =>
      apiFetch<{ entries: WorkflowAuditEntry[] }>(`/dashboard/audit-log${buildQuery(filters)}`),

    entityAuditLog: (entityType: string, entityId: string) =>
      apiFetch<{ entries: WorkflowAuditEntry[] }>(`/dashboard/audit-log/${entityType}/${entityId}`),

    score: () =>
      apiFetch<Record<string, unknown>>('/dashboard/score'),

    scoreHistory: () =>
      apiFetch<Record<string, unknown>>('/dashboard/score/history?days=90'),
  },

  reports: {
    attestationPdf: (taskId: string): string =>
      `/api/reports/attestation/${taskId}/pdf`,

    projectPdf: (projectId: string): string =>
      `/api/reports/project/${projectId}/pdf`,

    auditLogPdf: (limit?: number): string =>
      `/api/reports/audit-log/pdf${limit ? `?limit=${limit}` : ''}`,
  },

  connectors: {
    list: () =>
      apiFetch<{ connectors: TenantConnector[] }>('/connectors'),

    create: (input: { name: string; type: string; config: Record<string, unknown> }) =>
      apiFetch<TenantConnector>('/connectors', { method: 'POST', body: JSON.stringify(input) }),

    test: (id: string) =>
      apiFetch<{ healthy: boolean; latencyMs: number; error: string | null; testedAt: string }>(`/connectors/${id}/test`),

    browse: (id: string, path?: string) =>
      apiFetch<{ files: ConnectorFileEntry[]; path: string }>(`/connectors/${id}/browse${path ? `?path=${encodeURIComponent(path)}` : ''}`),

    remove: (id: string) =>
      apiFetch<{ success: boolean }>(`/connectors/${id}`, { method: 'DELETE' }),
  },

  policyReview: {
    analyze: (input: { projectId: string; connectorId: string; documentPaths: string[] }) =>
      apiFetch<{ sessionIds: string[]; totalSections: number; documentsAnalyzed: number }>(
        '/policy-review/analyze', { method: 'POST', body: JSON.stringify(input) },
      ),

    sessions: (filters?: ListFilters) =>
      apiFetch<{ sessions: PolicyReviewSession[] }>(`/policy-review/sessions${buildQuery(filters)}`),

    session: (id: string) =>
      apiFetch<{ session: PolicyReviewSession; sections: PolicySectionReview[] }>(`/policy-review/sessions/${id}`),

    acceptSection: (id: string) =>
      apiFetch<{ success: boolean; status: string }>(`/policy-review/sections/${id}/accept`, { method: 'PATCH' }),

    editSection: (id: string, finalText: string) =>
      apiFetch<{ success: boolean; status: string }>(`/policy-review/sections/${id}/edit`, {
        method: 'PATCH', body: JSON.stringify({ finalText }),
      }),

    rejectSection: (id: string) =>
      apiFetch<{ success: boolean; status: string }>(`/policy-review/sections/${id}/reject`, { method: 'PATCH' }),

    writeBack: (sessionId: string) =>
      apiFetch<{ success: boolean }>(`/policy-review/sessions/${sessionId}/write-back`, { method: 'POST' }),
  },

  ingest: {
    status: () => apiFetch<{ enabled: boolean; running?: boolean; adapterCount?: number; adapters?: Record<string, unknown> }>('/ingest/status'),
    adapters: () => apiFetch<{ adapters: Record<string, unknown>[] }>('/ingest/adapters'),
    runNow: (adapterId: string) => apiFetch<{ adapterId: string; itemsFetched: number; itemsNew: number; itemsDuplicate: number; classified: number; errors: number; durationMs: number }>(`/ingest/run/${adapterId}`, { method: 'POST' }),
  },

  admin: {
    tenant: {
      get: (): Promise<TenantRecord> =>
        apiFetch<TenantRecord>('/admin/tenant'),
    },

    reviewers: {
      list: (): Promise<ReviewerRecord[]> =>
        apiFetch<ReviewerRecord[]>('/admin/reviewers'),

      create: (input: RegisterReviewerInput): Promise<ReviewerRecord> =>
        apiFetch<ReviewerRecord>('/admin/reviewers', {
          method: 'POST',
          body: JSON.stringify(input),
        }),

      update: (
        id: string,
        input: Partial<Omit<RegisterReviewerInput, 'password'>>,
      ): Promise<ReviewerRecord> =>
        apiFetch<ReviewerRecord>(`/admin/reviewers/${id}`, {
          method: 'PATCH',
          body: JSON.stringify(input),
        }),

      delete: (id: string): Promise<void> =>
        apiFetch<void>(`/admin/reviewers/${id}`, { method: 'DELETE' }),

      toggleActive: (id: string, isActive: boolean): Promise<ReviewerRecord> =>
        apiFetch<ReviewerRecord>(`/admin/reviewers/${id}`, {
          method: 'PATCH',
          body: JSON.stringify({ isActive }),
        }),
    },

    connectors: {
      list: (): Promise<ConnectorConfig[]> =>
        apiFetch<ConnectorConfig[]>('/admin/connectors'),

      create: (input: ConnectorConfigInput): Promise<ConnectorConfig> =>
        apiFetch<ConnectorConfig>('/admin/connectors', {
          method: 'POST',
          body: JSON.stringify(input),
        }),

      delete: (id: string): Promise<void> =>
        apiFetch<void>(`/admin/connectors/${id}`, { method: 'DELETE' }),

      verify: (id: string): Promise<ConnectorConfig> =>
        apiFetch<ConnectorConfig>(`/admin/connectors/${id}/verify`, {
          method: 'POST',
        }),
    },

    overview: (): Promise<Record<string, unknown>> =>
      apiFetch<Record<string, unknown>>('/admin/overview'),

    tenants: (): Promise<Record<string, unknown>> =>
      apiFetch<Record<string, unknown>>('/admin/tenants'),

    approveTenant: (id: string): Promise<Record<string, unknown>> =>
      apiFetch<Record<string, unknown>>(`/admin/tenants/${id}/approve`, { method: 'PATCH' }),

    rejectTenant: (id: string, reason: string): Promise<Record<string, unknown>> =>
      apiFetch<Record<string, unknown>>(`/admin/tenants/${id}/reject`, { method: 'PATCH', body: JSON.stringify({ reason }) }),

    webinars: {
      list: (): Promise<Record<string, unknown>> =>
        apiFetch<Record<string, unknown>>('/admin/webinars'),
      create: (data: Record<string, unknown>): Promise<Record<string, unknown>> =>
        apiFetch<Record<string, unknown>>('/admin/webinars', { method: 'POST', body: JSON.stringify(data) }),
      update: (id: string, data: Record<string, unknown>): Promise<Record<string, unknown>> =>
        apiFetch<Record<string, unknown>>(`/admin/webinars/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
    },
  },

  // ── Tenant profile ──────────────────────────────────
  tenant: {
    profile: (): Promise<Record<string, unknown>> =>
      apiFetch<Record<string, unknown>>('/tenant/profile'),
    updateProfile: (data: Record<string, unknown>): Promise<Record<string, unknown>> =>
      apiFetch<Record<string, unknown>>('/tenant/profile', { method: 'PUT', body: JSON.stringify(data) }),
    staff: {
      list: (): Promise<Record<string, unknown>> =>
        apiFetch<Record<string, unknown>>('/tenant/staff'),
      create: (data: Record<string, unknown>): Promise<Record<string, unknown>> =>
        apiFetch<Record<string, unknown>>('/tenant/staff', { method: 'POST', body: JSON.stringify(data) }),
      update: (id: string, data: Record<string, unknown>): Promise<Record<string, unknown>> =>
        apiFetch<Record<string, unknown>>(`/tenant/staff/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
      delete: (id: string): Promise<Record<string, unknown>> =>
        apiFetch<Record<string, unknown>>(`/tenant/staff/${id}`, { method: 'DELETE' }),
    },
    calendar: {
      list: (): Promise<Record<string, unknown>> =>
        apiFetch<Record<string, unknown>>('/tenant/calendar'),
      create: (data: Record<string, unknown>): Promise<Record<string, unknown>> =>
        apiFetch<Record<string, unknown>>('/tenant/calendar', { method: 'POST', body: JSON.stringify(data) }),
      update: (id: string, data: Record<string, unknown>): Promise<Record<string, unknown>> =>
        apiFetch<Record<string, unknown>>(`/tenant/calendar/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
      delete: (id: string): Promise<Record<string, unknown>> =>
        apiFetch<Record<string, unknown>>(`/tenant/calendar/${id}`, { method: 'DELETE' }),
    },
    assignmentRules: {
      list: (): Promise<Record<string, unknown>> =>
        apiFetch<Record<string, unknown>>('/tenant/assignment-rules'),
      create: (data: Record<string, unknown>): Promise<Record<string, unknown>> =>
        apiFetch<Record<string, unknown>>('/tenant/assignment-rules', { method: 'POST', body: JSON.stringify(data) }),
      update: (id: string, data: Record<string, unknown>): Promise<Record<string, unknown>> =>
        apiFetch<Record<string, unknown>>(`/tenant/assignment-rules/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
      delete: (id: string): Promise<Record<string, unknown>> =>
        apiFetch<Record<string, unknown>>(`/tenant/assignment-rules/${id}`, { method: 'DELETE' }),
    },
  },

  // ── Onboarding ──────────────────────────────────────
  onboarding: {
    chat: (message: string, sessionId?: string): Promise<Record<string, unknown>> =>
      apiFetch<Record<string, unknown>>('/onboarding/chat', { method: 'POST', body: JSON.stringify({ message, sessionId }) }),
    session: (): Promise<Record<string, unknown>> =>
      apiFetch<Record<string, unknown>>('/onboarding/session'),
    complete: (): Promise<Record<string, unknown>> =>
      apiFetch<Record<string, unknown>>('/onboarding/complete', { method: 'POST' }),
  },

  // ── Document Classification (SOP Extension) ────────
  documents: {
    classifications: (type?: string): Promise<{ data: Record<string, unknown>[] }> =>
      apiFetch<{ data: Record<string, unknown>[] }>(`/documents/classifications${type ? `?document_type=${type}` : ''}`),
    classify: (connectorId: string, filePath: string): Promise<{ data: Record<string, unknown> }> =>
      apiFetch<{ data: Record<string, unknown> }>('/documents/classify', { method: 'POST', body: JSON.stringify({ connectorId, filePath }) }),
    confirm: (id: string, documentType?: string): Promise<{ success: boolean }> =>
      apiFetch<{ success: boolean }>(`/documents/classifications/${id}`, { method: 'PATCH', body: JSON.stringify({ confirmed: true, documentType }) }),
  },

  // ── Dependencies ───────────────────────────────────
  dependencies: {
    list: (confirmedOnly = false): Promise<{ data: Record<string, unknown>[] }> =>
      apiFetch<{ data: Record<string, unknown>[] }>(`/dependencies?confirmed_only=${confirmedOnly}`),
    discover: (policyDocumentId: string): Promise<{ data: Record<string, unknown>[] }> =>
      apiFetch<{ data: Record<string, unknown>[] }>('/dependencies/discover', { method: 'POST', body: JSON.stringify({ policyDocumentId }) }),
    confirm: (id: string, confirmed: boolean): Promise<{ success: boolean }> =>
      apiFetch<{ success: boolean }>(`/dependencies/${id}/confirm`, { method: 'PATCH', body: JSON.stringify({ confirmed }) }),
  },

  // ── Training ───────────────────────────────────────
  training: {
    // Staff
    myTraining: (): Promise<{ data: { pending: Record<string, unknown>[]; completed: Record<string, unknown>[]; overdue: Record<string, unknown>[] } }> =>
      apiFetch<{ data: { pending: Record<string, unknown>[]; completed: Record<string, unknown>[]; overdue: Record<string, unknown>[] } }>('/training/my-training'),
    getAssignment: (id: string): Promise<{ data: Record<string, unknown> }> =>
      apiFetch<{ data: Record<string, unknown> }>(`/training/assignments/${id}`),
    markViewed: (id: string): Promise<{ success: boolean }> =>
      apiFetch<{ success: boolean }>(`/training/assignments/${id}/view`, { method: 'POST' }),
    acknowledge: (id: string): Promise<{ success: boolean; evidenceId: string }> =>
      apiFetch<{ success: boolean; evidenceId: string }>(`/training/assignments/${id}/acknowledge`, { method: 'POST' }),
    // Admin
    listPackages: (status?: string): Promise<{ data: Record<string, unknown>[] }> =>
      apiFetch<{ data: Record<string, unknown>[] }>(`/training/packages${status ? `?status=${status}` : ''}`),
    getPackage: (id: string): Promise<{ data: { package: Record<string, unknown>; assignments: Record<string, unknown>[] } }> =>
      apiFetch<{ data: { package: Record<string, unknown>; assignments: Record<string, unknown>[] } }>(`/training/packages/${id}`),
    updatePackage: (id: string, updates: Record<string, unknown>): Promise<{ success: boolean }> =>
      apiFetch<{ success: boolean }>(`/training/packages/${id}`, { method: 'PATCH', body: JSON.stringify(updates) }),
    publishPackage: (id: string): Promise<{ success: boolean; assigned: number; roles: string[] }> =>
      apiFetch<{ success: boolean; assigned: number; roles: string[] }>(`/training/packages/${id}/publish`, { method: 'POST' }),
    archivePackage: (id: string): Promise<{ success: boolean }> =>
      apiFetch<{ success: boolean }>(`/training/packages/${id}/archive`, { method: 'POST' }),
    dashboard: (): Promise<{ data: Record<string, unknown> }> =>
      apiFetch<{ data: Record<string, unknown> }>('/training/dashboard'),
    listEvidence: (limit?: number): Promise<{ data: Record<string, unknown>[] }> =>
      apiFetch<{ data: Record<string, unknown>[] }>(`/training/evidence${limit ? `?limit=${limit}` : ''}`),
  },

  // ── Cascades ───────────────────────────────────────
  cascades: {
    list: (status?: string): Promise<{ data: Record<string, unknown>[] }> =>
      apiFetch<{ data: Record<string, unknown>[] }>(`/cascades${status ? `?status=${status}` : ''}`),
    get: (id: string): Promise<{ data: Record<string, unknown> }> =>
      apiFetch<{ data: Record<string, unknown> }>(`/cascades/${id}`),
    initiate: (sessionId: string): Promise<{ data: Record<string, unknown> }> =>
      apiFetch<{ data: Record<string, unknown> }>('/cascades/initiate', { method: 'POST', body: JSON.stringify({ sessionId }) }),
    status: (id: string): Promise<{ data: Record<string, unknown> }> =>
      apiFetch<{ data: Record<string, unknown> }>(`/cascades/${id}/status`),
  },
};
